'use client'

import { forwardRef } from 'react'
import { ChevronRight, Menu, Moon, Sun } from 'lucide-react'
import { RefreshControl } from '../BaseDashboard'
import { usePageChrome } from './pageChrome'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// The 63 px bar over every page (the design system's .topbar): breadcrumb
// on the left -- "Operations › CTT › Unified Entries", the last crumb in
// the foreground -- and on the right the page's refresh controls (from
// PageChromeContext), the environment chip, the notifications bell and the
// theme toggle. Sticky at the top of the content scroller; the shell
// publishes its height so the page's own sticky bars stack under it.
// Below lg the hamburger opens the sidebar drawer.

const ENV = process.env.NEXT_PUBLIC_APP_ENV ?? 'prod'

export const iconButtonClass = 'flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground'

export const Topbar = forwardRef<HTMLDivElement, {
  crumbs: string[]
  theme: ProcessedTheme
  onToggleTheme: () => void
  onOpenMenu: () => void
  bell?: React.ReactNode
}>(function Topbar({ crumbs, theme, onToggleTheme, onOpenMenu, bell }, ref) {
  const { refresh } = usePageChrome()
  const isDark = theme === 'dark'
  return (
    <div ref={ref} className="sticky top-0 z-30 flex h-[63px] shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4 lg:px-10">
      <div className="flex min-w-0 items-center gap-3">
        <button type="button" onClick={onOpenMenu} title="Open menu" className={`${iconButtonClass} lg:hidden`}>
          <Menu className="h-4 w-4" />
        </button>
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 font-mono text-label text-subtle">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1
            return (
              <span key={`${i}-${c}`} className="flex min-w-0 items-center gap-2">
                {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
                <span className={`truncate ${last ? 'font-medium text-foreground' : ''}`}>{c}</span>
              </span>
            )
          })}
        </nav>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {refresh && <RefreshControl theme={theme} lastUpdated={refresh.lastUpdated} onRefresh={refresh.onRefresh} refreshing={refresh.refreshing} storageKey={refresh.storageKey} />}
        <span className="hidden items-center gap-1.5 rounded-[4px] border border-border px-2 py-1 font-mono text-label text-muted-foreground sm:flex" title="Environment">
          <span className={`h-[5px] w-[5px] rounded-full ${ENV === 'prod' ? 'bg-success' : 'bg-warning'}`} />
          {ENV}
        </span>
        <span className="hidden h-4 w-px bg-border sm:block" />
        {bell}
        <button type="button" onClick={onToggleTheme} title={isDark ? 'Switch to day mode' : 'Switch to night mode'} className={iconButtonClass}>
          {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  )
})
